import { createFileRoute } from "@tanstack/react-router";
import { useEffect, lazy, Suspense, useState } from "react";
import { Header } from "@/components/Header";
import { HeroUpload } from "@/components/HeroUpload";

const Footer = lazy(() => import("@/components/Footer").then((m) => ({ default: m.Footer })));
const TrustBar = lazy(() => import("@/components/TrustBar").then((m) => ({ default: m.TrustBar })));
const TrustSocialProof = lazy(() =>
  import("@/components/TrustSocialProof").then((m) => ({ default: m.TrustSocialProof })),
);
const WhatYouGet = lazy(() =>
  import("@/components/WhatYouGet").then((m) => ({ default: m.WhatYouGet })),
);
const RoleSelector = lazy(() =>
  import("@/components/RoleSelector").then((m) => ({ default: m.RoleSelector })),
);

const ContractAnalyzer = lazy(() =>
  import("@/components/ContractAnalyzer").then((m) => ({ default: m.ContractAnalyzer })),
);
import {
  FileSearch,
  ShieldAlert,
  Home as HomeIcon,
  ArrowRight,
  Sparkles,
  Upload,
  ScanLine,
  FileCheck2,
  Lock,
  CircleCheck,
  AlertTriangle,
  Scale,
  MessageCircleQuestion,
  Lightbulb,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import { SampleReportPreview } from "@/components/SampleReportPreview";
import { TrustSignalFlow } from "@/components/TrustSignalFlow";
import { AskAIDemo } from "@/components/AskAIDemo";
import { CredibilityWall } from "@/components/CredibilityWall";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "legaldecoder — Understand Your Contract Before You Sign" },
      {
        name: "description",
        content:
          "AI-powered lease, NDA, and freelance contract analysis for everyday people. Spot risky clauses, decode legal language, and protect yourself in seconds.",
      },
      { property: "og:title", content: "legaldecoder — Decode Any Contract in Seconds" },
      {
        property: "og:description",
        content:
          "Upload a lease, NDA, or freelance contract. Get plain-English red flags, key terms, and next steps — instantly.",
      },
      { property: "og:url", content: "https://legaldecoder.io/" },
    ],
    links: [{ rel: "canonical", href: "https://legaldecoder.io/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "legaldecoder",
          url: "https://legaldecoder.io/",
          logo: "https://legaldecoder.io/favicon.png",
          description:
            "AI-powered contract analysis that decodes legal documents into plain English for renters, freelancers, and small business owners.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "legaldecoder",
          url: "https://legaldecoder.io/",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://legaldecoder.io/clauses?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Index,
});

function Index() {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      toast.success("You're Pro now! Premium tools are unlocked.", { duration: 6000 });
      const url = new URL(window.location.href);
      url.searchParams.delete("checkout");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const isCapacitor = isMounted && typeof window !== "undefined" && !!(window as any).Capacitor;

  if (isCapacitor) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 flex flex-col justify-center py-6">
          <section id="analyzer" className="mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8">
            <Suspense fallback={<div className="min-h-[400px]" aria-hidden />}>
              <ContractAnalyzer />
            </Suspense>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <HeroUpload />
        <Suspense fallback={null}>
          <RoleSelector />
          <TrustSocialProof />
        </Suspense>
        <section id="analyzer" className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
          <Suspense fallback={<div className="min-h-[400px]" aria-hidden />}>
            <ContractAnalyzer />
          </Suspense>
        </section>
        <TrustBadges />
        <CredibilityWall />
        <TrustSignalFlow />
        <HowItWorks />
        <AskAIDemo />
        <UseCases />
        <CoreValue />
        <SampleReport />
        <WhyItMatters />
        <LegalDisclaimerBand />
        <Suspense fallback={null}>
          <TrustBar />
          <FAQ />
          <WhatYouGet />
          <Footer />
        </Suspense>
      </main>
    </div>
  );
}

const trustBadges = [
  { icon: Lock, label: "Zero data retention" },
  { icon: ShieldAlert, label: "In-memory analysis" },
  { icon: HomeIcon, label: "Built for renters & freelancers" },
  { icon: CircleCheck, label: "No signup to try a sample" },
];

function TrustBadges() {
  return (
    <section className="border-y border-border bg-surface/60 py-6">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {trustBadges.map((b) => (
            <li
              key={b.label}
              className="flex items-center gap-2 text-xs font-medium text-muted-foreground sm:text-sm"
            >
              <b.icon className="h-4 w-4 text-primary-glow" strokeWidth={2.25} />
              {b.label}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const steps = [
  {
    icon: Upload,
    title: "Upload your document",
    desc: "Drop in a PDF, snap a photo of a paper contract, or paste plain text. Residential leases, commercial leases, NDAs, freelance and contractor agreements, employment offer letters, service agreements, purchase agreements — if it has clauses, we can decode it. Files are processed in-memory and discarded immediately. We never store the document, never train AI on it, and never share it with third parties.",
  },
  {
    icon: ScanLine,
    title: "We analyze every clause",
    desc: "Our AI reads the contract end-to-end and benchmarks every clause against thousands of real-world examples. It surfaces predatory terms (illegal fees, automatic renewals, one-sided indemnification), hidden obligations (security deposit traps, scope creep, unpaid IP transfers), and ambiguous legalese that a landlord, client, or HR team might exploit. Each finding is rated High, Medium, or Low risk so you can prioritize what to push back on first.",
  },
  {
    icon: FileCheck2,
    title: "Get a plain-English summary",
    desc: "You'll see a clear verdict (sign, negotiate, or walk away), a list of key terms (dates, amounts, deadlines, obligations), color-coded red flags with explanations, and a tailored set of questions to ask before signing. If the contract is unusually risky, we'll point you toward a licensed attorney in your state — never a robot pretending to be one.",
  },
];

function HowItWorks() {
  return (
    <section className="py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-12 max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">How it works</h2>
          <p className="mt-3 text-muted-foreground">Three steps. No legalese. No commitment.</p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {steps.map((step, i) => (
            <div
              key={step.title}
              className="relative rounded-2xl border border-border bg-card p-6 shadow-card"
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow shadow-elegant">
                  <step.icon className="h-5 w-5 text-primary-foreground" strokeWidth={2.25} />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Step {i + 1}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-foreground">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const useCases = [
  {
    id: "renter",
    targetId: "use-case-renter",
    icon: HomeIcon,
    name: "Lease Decoder",
    desc: "Spot hidden fees, illegal clauses, and tenant-unfriendly terms in apartment leases.",
    href: "/tools/lease-decoder",
    color: "from-primary-glow to-accent",
  },
  {
    id: "entrepreneur",
    targetId: "use-case-entrepreneur",
    icon: FileCheck2,
    name: "Service Agreement Decoder",
    desc: "Review vendor deals, service contracts, payment obligations, renewals, and scope language before committing.",
    href: "/document-types/service-agreement",
    color: "from-primary to-accent",
  },
  {
    id: "buyer",
    targetId: "use-case-buyer",
    icon: HomeIcon,
    name: "Purchase Agreement Review",
    desc: "Decode contingencies, disclosures, escalation clauses, and closing obligations before buying a home.",
    href: "/document-types/purchase-agreement",
    color: "from-accent to-primary",
  },
  {
    id: "nda",
    targetId: "use-case-nda",
    icon: ShieldAlert,
    name: "NDA Scanner",
    desc: "Understand exactly what you can and cannot say after signing — and for how long.",
    href: "/tools/nda-scanner",
    color: "from-accent to-primary-glow",
  },
  {
    id: "freelancer",
    targetId: "use-case-freelancer",
    icon: FileSearch,
    name: "Freelancer Protector",
    desc: "Catch unpaid IP transfers, scope creep, and unfair payment terms before you commit.",
    href: "/tools/freelancer-protector",
    color: "from-primary to-primary-glow",
  },
  {
    id: "hr",
    targetId: "use-case-hr",
    icon: FileCheck2,
    name: "Employment Agreement Checker",
    desc: "Check offer letters, non-competes, severance, arbitration, and workplace IP terms in plain English.",
    href: "/tools/employment-agreement-checker",
    color: "from-primary-glow to-primary",
  },
];

function UseCases() {
  const [selectedRole, setSelectedRole] = useState("renter");

  useEffect(() => {
    const handleRoleSelected = (event: Event) => {
      const roleId = (event as CustomEvent<{ roleId?: string }>).detail?.roleId;
      if (roleId) setSelectedRole(roleId);
    };

    document.addEventListener("legaldecoder-role-selected", handleRoleSelected);
    return () => document.removeEventListener("legaldecoder-role-selected", handleRoleSelected);
  }, []);

  return (
    <section className="bg-surface/40 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-12 max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Built for the contracts you actually sign
          </h2>
          <p className="mt-3 text-muted-foreground">
            Specialized decoders tuned for the documents that matter most.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {useCases.map((tool) => {
            const active = selectedRole === tool.id;
            return (
              <a
                key={tool.name}
                id={tool.targetId}
                href={tool.href}
                className={
                  "group scroll-mt-28 rounded-2xl border bg-card p-6 shadow-card transition-all hover:-translate-y-1 hover:shadow-card-hover " +
                  (active
                    ? "border-primary ring-2 ring-primary/20 shadow-card-hover"
                    : "border-border")
                }
              >
                <div
                  className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${tool.color} shadow-elegant`}
                >
                  <tool.icon className="h-6 w-6 text-primary-foreground" strokeWidth={2.25} />
                </div>
                <h3 className="text-lg font-semibold text-foreground">{tool.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{tool.desc}</p>
                <div className="mt-4 flex items-center gap-1 text-sm font-medium text-primary-glow">
                  Try it now
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const valueProps = [
  {
    icon: MessageCircleQuestion,
    emoji: "💬",
    title: "Plain-English explanations",
    desc: "No legalese. We translate every clause into language a normal human can understand.",
    accent: "from-sky-100 to-blue-50",
    iconColor: "text-sky-600",
  },
  {
    icon: AlertTriangle,
    emoji: "🚩",
    title: "Risk highlights",
    desc: "Color-coded red flags so you instantly see what to push back on — and what's standard.",
    accent: "from-rose-100 to-orange-50",
    iconColor: "text-rose-600",
  },
  {
    icon: FileCheck2,
    emoji: "📋",
    title: "Clause summaries",
    desc: "Key terms surfaced up front: dates, amounts, obligations, deadlines. No surprises.",
    accent: "from-emerald-100 to-green-50",
    iconColor: "text-emerald-600",
  },
  {
    icon: Lightbulb,
    emoji: "💡",
    title: "Suggested questions to ask",
    desc: "Walk into the conversation with the right questions for your landlord, client, or HR.",
    accent: "from-amber-100 to-yellow-50",
    iconColor: "text-amber-600",
  },
  {
    icon: Lock,
    emoji: "🔒",
    title: "Privacy-first processing",
    desc: "Documents are analyzed in-memory and discarded. We never store or train on your contract.",
    accent: "from-violet-100 to-purple-50",
    iconColor: "text-violet-600",
  },
  {
    icon: Scale,
    emoji: "⚖️",
    title: "Action-oriented results",
    desc: "Should you sign? What should you negotiate? We tell you — not just what the document says.",
    accent: "from-indigo-100 to-blue-50",
    iconColor: "text-indigo-600",
  },
];

function CoreValue() {
  return (
    <section className="py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-12 max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Everything you need, nothing you don't
          </h2>
          <p className="mt-3 text-muted-foreground">Practical answers, not 40-page legal memos.</p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {valueProps.map((v) => (
            <div
              key={v.title}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-7 shadow-card transition-all hover:-translate-y-1 hover:shadow-card-hover"
            >
              <div
                className={`pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-gradient-to-br ${v.accent} opacity-60 blur-2xl transition-opacity group-hover:opacity-90`}
                aria-hidden
              />
              <div className="relative">
                <div
                  className={`mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${v.accent} text-4xl shadow-sm transition-transform group-hover:scale-110 group-hover:rotate-3`}
                  aria-hidden
                >
                  {v.emoji}
                </div>
                <h3 className="text-lg font-semibold text-foreground">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SampleReport() {
  return (
    <section className="bg-surface/40 py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 text-primary-glow" />
            Sample report
          </div>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            See what a legaldecoder report looks like
          </h2>
          <p className="mt-3 text-muted-foreground">
            A real example from a 12-month residential lease. This is what you'll get in seconds.
          </p>
        </div>

        <SampleReportPreview />

        <div className="mt-8 flex flex-col items-center gap-3">
          <p className="text-center text-sm text-muted-foreground">
            Want this kind of clarity for your own contract?
          </p>
          <a
            href="#analyzer"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById("analyzer");
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                // Focus the upload area for immediate action
                setTimeout(() => {
                  const input = el.querySelector<HTMLElement>(
                    'input[type="file"], button, [role="button"]',
                  );
                  input?.focus();
                }, 600);
              }
            }}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition-all hover:scale-105 hover:shadow-xl"
          >
            Analyze Your Contract Now
            <ArrowRight className="h-4 w-4" />
          </a>
          <p className="text-xs text-muted-foreground">
            Free · No signup required · Results in seconds
          </p>
        </div>
      </div>
    </section>
  );
}

const faqs = [
  {
    q: "Is this legal advice?",
    a: "No. legaldecoder is an AI-powered explainer that translates contract language into plain English and flags potentially risky clauses. It is for informational and educational purposes only and is not a substitute for advice from a licensed attorney in your jurisdiction. For high-stakes decisions — buying a home, signing a non-compete, settling a dispute — talk to a lawyer. We'll even help you find one in your state.",
  },
  {
    q: "Is my document stored anywhere?",
    a: "No. We operate on a strict zero-data-retention policy. Your contract is processed in-memory by our analysis pipeline and discarded immediately after the result is returned to your browser. We never write the document to disk, never log its contents, and never use it to train AI models. Your privacy is the product.",
  },
  {
    q: "How does the AI actually analyze my contract?",
    a: "When you upload, we extract the text (using OCR if it's an image or scan), break the document into individual clauses, and run each clause through a large language model fine-tuned with thousands of real-world contract examples. The model classifies each clause by type (payment, termination, IP, liability, etc.), benchmarks it against typical industry terms, and flags anything unusually one-sided or risky. The result is assembled into a structured report you can read in under two minutes.",
  },
  {
    q: "How accurate is the analysis?",
    a: "Our AI is highly capable at surfacing common red flags, key terms, and predatory clauses across leases, NDAs, freelance agreements, and employment contracts. That said, no AI is perfect — laws vary by state and country, and edge cases happen. Treat the report as a smart first pass that helps you ask the right questions, not as the final word. For anything material, verify with a licensed attorney.",
  },
  {
    q: "What file types and sizes are supported?",
    a: "PDFs up to 15 MB, JPEG and PNG photos of paper contracts (we'll OCR them automatically), and pasted plain text. If your PDF is a scan rather than a true text PDF, the photo / image path usually produces cleaner results. Multi-page contracts are fine — we stitch the pages back together before analysis.",
  },
  {
    q: "What kinds of contracts can I decode?",
    a: "Residential leases, commercial leases, non-disclosure agreements (mutual and one-way), freelance and independent contractor agreements, employment offer letters, non-competes, service agreements, vendor contracts, and home purchase agreements. We have specialized decoders tuned for the most common document types, plus a general-purpose mode for anything else.",
  },
  {
    q: "What does the report actually include?",
    a: "A plain-English verdict (sign, negotiate, or walk away), a list of key terms with the numbers that matter (rent, deposit, term length, payment dates, deadlines), color-coded red flags rated High / Medium / Low risk with an explanation of why each one matters, and a tailored list of questions to ask your landlord, client, employer, or counterparty before you sign.",
  },
  {
    q: "Do I have to sign up?",
    a: "You can view a full sample analysis with no signup at all. To analyze your own document you'll create a free account, which gives you 3 analyses per month. Upgrade to Pro ($9.99/month) for unlimited analyses, contract comparison, clause history, and the full clause library.",
  },
  {
    q: "How is legaldecoder different from just pasting my contract into ChatGPT?",
    a: "Generic chatbots will summarize your contract, but they don't know what's normal for a residential lease in your state, what a freelance scope-creep clause typically looks like, or which NDA terms courts have struck down. legaldecoder is purpose-built for contract analysis: it benchmarks every clause against real-world precedent, ranks risk consistently, and never trains on your document. You also get a structured report you can save, share, and revisit — not an ephemeral chat thread.",
  },
  {
    q: "What about my privacy and security?",
    a: "Documents are transmitted over TLS (HTTPS), processed in an ephemeral compute environment, and discarded the moment analysis completes. We don't sell data, don't run ad-tracking pixels on your reports, and don't share contract contents with any third party. Account email and billing data are stored only to run your account — see our Privacy Policy for the full breakdown.",
  },
  {
    q: "What if my contract is in another language?",
    a: "Today legaldecoder is optimized for English-language contracts (US, UK, and Canadian conventions). If you upload a contract in another language, the analysis may still surface obvious red flags, but accuracy will be lower. We're actively working on first-class Spanish and French support.",
  },
  {
    q: "Can I share or save my analysis?",
    a: "Yes. Every analysis can be exported as a PDF or shared via a private link with someone you trust — a partner, attorney, or business advisor. Pro users can also keep a full history of past analyses and compare two versions of a contract side-by-side to see exactly what changed during negotiation.",
  },
];

function FAQ() {
  return (
    <section className="py-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Frequently asked questions
          </h2>
          <p className="mt-3 text-muted-foreground">
            Quick answers to the things people ask before uploading.
          </p>
        </div>
        <Accordion
          type="single"
          collapsible
          className="rounded-2xl border border-border bg-card px-6 shadow-card"
        >
          {faqs.map((item) => (
            <AccordionItem key={item.q} value={item.q} className="last:border-b-0">
              <AccordionTrigger className="py-5 text-left text-base font-semibold">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-sm leading-relaxed text-muted-foreground">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function WhyItMatters() {
  return (
    <section className="bg-surface/40 py-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Why understanding your contract matters
          </h2>
          <p className="mt-3 text-muted-foreground">
            A short, plain-English guide to the documents you sign — and why a second pair of eyes
            (even an AI one) can save you thousands.
          </p>
        </div>

        <div className="space-y-10 text-base leading-relaxed text-foreground">
          <article>
            <h3 className="text-xl font-semibold">The hidden cost of "just signing it"</h3>
            <p className="mt-3 text-muted-foreground">
              Most people sign contracts they haven't fully read. A 2022 Deloitte survey found that
              91% of consumers accept terms and conditions without reading them, and renters fare
              only slightly better — the average tenant spends under four minutes reviewing a
              multi-page lease before signing. That's a problem, because the clauses most likely to
              cost you money (automatic rent escalators, joint-and-several liability, waiver of jury
              trial, mandatory arbitration, non-refundable "administrative" fees) are almost always
              buried in the back half of the document, written in dense legalese designed to be
              skimmed past.
            </p>
            <p className="mt-3 text-muted-foreground">
              The result: thousands of renters every year forfeit security deposits they were
              legally entitled to, freelancers hand over intellectual property worth far more than
              the project paid, and new employees sign non-competes so broad they block their next
              job. None of that happens because people are careless. It happens because contracts
              are deliberately hard to read, and hiring a lawyer for every document is both
              expensive and slow.
            </p>
          </article>

          <article>
            <h3 className="text-xl font-semibold">Leases: where landlords most often overreach</h3>
            <p className="mt-3 text-muted-foreground">
              Residential leases are the contracts most people sign most often, and they're also
              where unenforceable clauses appear most frequently. Common offenders include
              "no-pets-ever" clauses that ignore service animal protections, blanket waivers of the
              warranty of habitability (illegal in most states), early termination penalties that
              exceed actual damages, security deposit deductions for normal wear and tear, and entry
              rights that bypass the 24-hour notice your state likely requires. A good first read of
              your lease will surface these so you can either negotiate them out before signing or
              know your rights if the landlord tries to enforce them later.
            </p>
            <p className="mt-3 text-muted-foreground">
              State-by-state rules vary enormously — California, New York, Oregon, Washington, and
              New Jersey all have strong tenant protections, while many southern states tilt heavily
              toward landlords. legaldecoder flags clauses that conflict with the state-specific
              rules where your unit is located, so you're not guessing whether a term is
              enforceable.
            </p>
          </article>

          <article>
            <h3 className="text-xl font-semibold">NDAs: what you're really agreeing not to say</h3>
            <p className="mt-3 text-muted-foreground">
              Non-disclosure agreements have quietly become one of the most-signed contracts in
              modern professional life — every job interview, every freelance pitch, every investor
              conversation. Most are harmless. A meaningful minority are not. The riskiest NDA terms
              are perpetual confidentiality (no end date), overly broad definitions of "confidential
              information" that sweep in publicly known facts, non-disparagement clauses that
              prevent you from describing your own work, and one-way liquidated damages clauses that
              put a six- or seven-figure number on any future breach. A two-minute scan can tell you
              whether an NDA is standard market practice or a trap.
            </p>
          </article>

          <article>
            <h3 className="text-xl font-semibold">
              Freelance and contractor agreements: where IP and payment go wrong
            </h3>
            <p className="mt-3 text-muted-foreground">
              Independent contractors get squeezed in two predictable places: intellectual property
              and payment terms. On IP, the default "work made for hire" plus "assignment of all
              related IP" combination can mean you hand over rights to tools, frameworks, and ideas
              you developed long before the engagement began. On payment, watch for net-60 or net-90
              terms with no late fee, "pay when paid" clauses tied to the client's own customers,
              kill fees that don't cover work already performed, and indemnification clauses that
              make you personally liable for the client's misuse of your work. None of these are
              unfightable — but you have to spot them before you sign.
            </p>
          </article>

          <article>
            <h3 className="text-xl font-semibold">How AI changes the math</h3>
            <p className="mt-3 text-muted-foreground">
              A licensed attorney is still the right call for anything material — a home purchase, a
              partnership agreement, a severance package, a lawsuit. But for the dozens of routine
              contracts every adult signs each year, hiring a lawyer is unrealistic. AI-powered
              analysis fills that gap. It won't replace your attorney, but it will tell you when you
              actually need one, and it will hand you the right questions to ask before that call.
              That's the bet legaldecoder is built on: more people reading their contracts, fewer
              people getting blindsided.
            </p>
          </article>
        </div>
      </div>
    </section>
  );
}

function LegalDisclaimerBand() {
  return (
    <section className="border-y border-border bg-amber-50/60 py-6 dark:bg-amber-950/20">
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <p className="text-xs font-semibold uppercase tracking-wider text-amber-900 dark:text-amber-200">
          Not Legal Advice — For Informational Purposes Only
        </p>
        <p className="mt-2 text-sm leading-relaxed text-amber-900/80 dark:text-amber-100/80">
          legaldecoder is an educational tool that uses AI to translate contract language into plain
          English. It does not create an attorney-client relationship and is not a substitute for
          advice from a licensed attorney in your jurisdiction. For any decision with significant
          financial or legal consequences, consult a qualified lawyer in your state or country.
        </p>
      </div>
    </section>
  );
}
